import { appNameFromPath } from "../runtime/stage-router.js";
import { OWNER_STAGE_LABELS, OWNER_STAGE_NAMES, ownerStagePath } from "../stages/owner-stage-routes.js";
import { resolveStageCatalogEntry, stageIconMarkup, STAGE_GROUPS } from "./stage-catalog.js";
import { escapeHtml } from "../util/escape-html.js";
import nodeMarkUrl from "../skin/brand/node-mark.jpg?url";
const HAMBURGER_ICON_PATH = '<path d="M4 6 H16 M4 10 H16 M4 14 H16"/>';
/** The five owner stages, in `OWNER_STAGE_NAMES` order — same source `pathForApp`/label
 * pair `topbar.ts`'s `TB_LINKS` and `pmenu.ts`'s primary dock both already derive theirs
 * from (`owner-stage-routes.ts`). Unlike `pmenu.ts`'s `PMENU_PRIMARY_ITEMS` (a strict
 * Home/Agents/Friends 3-item subset — Wallet/Profile deliberately excluded there, see that
 * module's own doc comment), the mobile sheet includes all five: the source's own mobile
 * panel (`renderMobileMenuFromRegistry()`, `core/shell/router/index.js` ~161-180) filters
 * its registry items on `item.mobile !== false` only, NOT on `more`/`primary` — and
 * dashboard(Home)/agents/friends/profile all carry `more:false` (excluded from the desktop
 * `.pmore` flyout) while still passing that mobile filter, and wallet has neither flag set,
 * so the real source mobile sheet lists all five inline within their normal catalog groups
 * (Home/Friends -> Default, Agents -> Agents, Wallet -> Network, Profile -> System) rather
 * than carving out a separate "primary" section the way the desktop dock does. `.tb-center`/
 * `.pmenu-id`/`.wallet-status` are the ONLY places those two (wallet/profile) surface on
 * desktop; the mobile sheet is their sole nav path once `.topbar` is retired as mobile chrome
 * (`legacy-numbered.css`'s `@media (max-width:768px){.topbar{display:none !important}}`).
 */
const OWNER_NAV_ITEMS = OWNER_STAGE_NAMES.map((name) => ({
    label: OWNER_STAGE_LABELS[name],
    path: ownerStagePath(name),
}));
/**
 * Port of the original skin's `.mobile-topbar` + `.mobile-hamburger` + `.mobile-menu-panel`
 * + `.mobile-menu-backdrop` — the real <769px mobile chrome (`.node-design/original-skin/
 * core/shell/router/index.js` ~149-292, styled by `stages/others/network/styles/network.css`
 * — vendored as `skin/css/mobile-nav.css`) — epic #1507 fidelity F8.
 *
 * Before this task the shell had no mobile-specific chrome at all: below 769px the desktop
 * `.topbar` text-link strip (`topbar.ts`) was the only nav surface left standing once
 * `.pmenu`/`.wallet-status` hide (`legacy-numbered.css`'s existing `@media (max-width:768px)`
 * rules) — and `.tb-center`'s five owner links plus every registry-installed stage app,
 * rendered as plain inline text buttons with no wrapping/scroll affordance, routinely
 * overflowed the viewport ~2.3x. `.topbar` is now ALSO hidden at <=768px (see
 * legacy-numbered.css's own F8 addendum) — this element is its full replacement, matching
 * the source's real default configuration (no `topbar-mode` class on `.shell`, `.topbar`
 * itself unused chrome — see topbar.ts's own class doc for its one remaining documented
 * fallback role).
 *
 * Rendered UNCONDITIONALLY (like `.pmenu`/`.topbar` themselves) — visibility below/above the
 * 769px breakpoint is entirely CSS-media-query-driven (`mobile-nav.css`), not JS-computed,
 * matching every other breakpoint-gated chrome piece in this shell.
 *
 * The sheet's content is this module's own `combinedMobileNavItems()`: the five owner stages
 * (`OWNER_NAV_ITEMS`, see its own doc comment for why all five rather than the desktop dock's
 * 3-item subset) followed by the same registry-installed `NavItem[]` `items` feeds
 * `<node-shell-pmenu>`'s `.pmore` flyout, grouped into `stage-catalog.ts`'s five colored
 * groups (`STAGE_GROUPS`, epic #1507 fidelity F5) — reusing that module rather than
 * reinventing a second grouping table, per this task's brief.
 *
 * Open/close/focus management mirrors `pmenu.ts`'s already-shipped `.pmore` flyout pattern
 * (the closest existing analog — a toggle panel owned by the same custom element as its
 * trigger, not a separately mounted/unmounted modal like `node-dialog.ts`): `aria-hidden` +
 * `inert` on the panel while closed, previous-focus capture/restore around a
 * backdrop-tap/Escape close, and no focus restoration on an entry-selection close (app-
 * shell.ts's own post-navigation `#focusOutlet()` already owns that outcome — see
 * `pmenu.ts`'s `#closePmore` doc comment for the identical reasoning).
 */
export class NodeShellMobileNavElement extends HTMLElement {
    #currentPath = "/settings";
    #items = [];
    #open = false;
    #previousFocus = null;
    connectedCallback() {
        this.addEventListener("click", this.#handleClick);
        this.addEventListener("keydown", this.#handleKeyDown);
        this.render();
    }
    disconnectedCallback() {
        this.removeEventListener("click", this.#handleClick);
        this.removeEventListener("keydown", this.#handleKeyDown);
    }
    set currentPath(value) {
        this.#currentPath = value;
        this.render();
    }
    set items(value) {
        this.#items = value;
        this.render();
    }
    render() {
        const currentPath = this.#currentPath;
        const open = this.#open;
        this.innerHTML = `
      <div class="mobile-topbar" id="mobileTopbar">
        <img class="mt-logo-img" src="${nodeMarkUrl}" alt="Node">
      </div>
      <button
        class="mobile-hamburger"
        id="mobileHamburger"
        type="button"
        aria-label="${open ? "Close menu" : "Open menu"}"
        aria-expanded="${open ? "true" : "false"}"
        aria-controls="mobileMenuPanel"
        data-mobile-hamburger
      ><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round">${HAMBURGER_ICON_PATH}</svg></button>
      <div
        class="mobile-menu-panel${open ? " open" : ""}"
        id="mobileMenuPanel"
        aria-hidden="${open ? "false" : "true"}"
        ${open ? "" : "inert"}
        data-mobile-menu-panel
      >
        <div class="mmp-inner">${renderMobileGroups(combinedMobileNavItems(this.#items), currentPath)}</div>
      </div>
      <div class="mobile-menu-backdrop${open ? " open" : ""}" data-mobile-menu-backdrop></div>
    `;
        if (open) {
            queueMicrotask(() => {
                if (this.isConnected && this.#open) {
                    this.querySelector(".mmp-item")?.focus();
                }
            });
        }
    }
    #handleClick = (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        if (target.closest("[data-mobile-hamburger]")) {
            if (this.#open) {
                this.#close();
            }
            else {
                this.#openMenu();
            }
            return;
        }
        if (target.closest("[data-mobile-menu-backdrop]")) {
            this.#close();
            return;
        }
        const item = target.closest(".mmp-item[data-path]");
        if (item) {
            const path = item.dataset.path;
            // Selecting an entry also navigates — mirrors `pmenu.ts`'s own `.pmore` entry click
            // (`#closePmore({ restoreFocus: false })`): app-shell.ts's post-navigation
            // `#focusOutlet()` already moves focus into the newly-mounted stage, so restoring
            // focus to the hamburger here would just get silently overridden a moment later.
            this.#close({ restoreFocus: false });
            this.dispatchEvent(new CustomEvent("navigate", {
                bubbles: true,
                composed: true,
                detail: { path },
            }));
        }
    };
    #handleKeyDown = (event) => {
        if (!(event instanceof KeyboardEvent)) {
            return;
        }
        if (event.key === "Escape" && this.#open) {
            this.#close();
        }
    };
    #openMenu() {
        if (this.#open) {
            return;
        }
        const active = this.#activeElement();
        this.#previousFocus = active instanceof HTMLElement ? active : null;
        this.#open = true;
        this.render();
    }
    #close(options = {}) {
        if (!this.#open) {
            return;
        }
        const restoreFocus = options.restoreFocus ?? true;
        this.#open = false;
        this.render();
        const fallbackFocus = this.#previousFocus;
        this.#previousFocus = null;
        if (!restoreFocus) {
            return;
        }
        queueMicrotask(() => {
            if (!this.isConnected) {
                return;
            }
            const target = this.querySelector("[data-mobile-hamburger]") ?? fallbackFocus;
            target?.focus();
        });
    }
    #activeElement() {
        const root = this.getRootNode();
        return root instanceof ShadowRoot ? root.activeElement : this.ownerDocument.activeElement;
    }
}
/** `OWNER_NAV_ITEMS` (Home/Wallet/Friends/Agents/Profile) followed by the registry-installed
 * stage apps (`items`, same source as `.pmore`'s own list) — see the class doc for why the
 * mobile sheet includes all five owner stages rather than the desktop dock's 3-item subset. */
function combinedMobileNavItems(items) {
    return [...OWNER_NAV_ITEMS, ...items];
}
/** One `.mmp-head` + its `.mmp-item` rows per non-empty `stage-catalog.ts` group, in
 * `STAGE_GROUPS` order — the same grouping `pmore.ts#renderGroups` uses for `.pmore-sec`,
 * reused here per this task's brief rather than a second grouping table. */
function renderMobileGroups(items, currentPath) {
    return STAGE_GROUPS.map((group) => {
        const groupItems = items.filter((item) => resolveStageCatalogEntry(appNameFromPath(item.path)).group === group.id);
        if (groupItems.length === 0) {
            return "";
        }
        return `
      <div class="mmp-head">${escapeHtml(group.label)}</div>
      ${groupItems.map((item) => renderMobileEntry(item, currentPath)).join("")}
    `;
    }).join("");
}
function renderMobileEntry(item, currentPath) {
    const current = item.path === currentPath;
    const icon = resolveStageCatalogEntry(appNameFromPath(item.path)).icon;
    return `
    <button
      class="mmp-item${current ? " active" : ""}"
      data-path="${item.path}"
      aria-current="${current ? "page" : "false"}"
      type="button"
    ><svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${stageIconMarkup(icon)}</svg><span>${escapeHtml(item.label)}</span></button>
  `;
}
//# sourceMappingURL=mobile-nav.js.map